import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, LinearProgress, Button, Chip } from '@mui/material';
import {
  Payments,
  ChevronRight,
  WarningAmber,
  Receipt,
  AccessTime,
} from '@mui/icons-material';
import {
  ComposedChart,
  Bar,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import hrTimePayrollService from '../../services/hrTimePayrollService';
import { useControlRoomColors, ControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle, categoryAccent } from '../../dashboard/chartTokens';
import { formatIndianNumber } from '../../utils/currency';
import { ErrorState, TableEmptyState, StatusDot, StatusTone, TableSurface, headerCellSx, bodyRowSx } from '../../components/common/pageKit';

// ---- pure formatters ----
const isNum = (v: any) => v !== null && v !== undefined && v !== '' && !isNaN(Number(v));
const fmtMoney = (v: any) => (isNum(v) ? formatIndianNumber(Number(v)) : '—');
const fmtInt = (v: any) => (isNum(v) ? Number(v).toLocaleString('en-IN') : '—');
const fmtDate = (v: any) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const RUN_STATUS_TONE: Record<string, StatusTone> = {
  DRAFT: 'neutral',
  PROCESSING: 'info',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'info',
  PAID: 'success',
  CANCELLED: 'error',
};

const runStatusTone = (status?: string): StatusTone => RUN_STATUS_TONE[(status || '').toUpperCase()] || 'neutral';

const severityTone = (C: ControlRoomColors, severity?: string): string => {
  const s = (severity || '').toLowerCase();
  if (s === 'critical') return C.bad;
  if (s === 'warning') return C.warn;
  return C.cyan;
};

const TimePayrollCommandCenter: React.FC = () => {
  const navigate = useNavigate();
  const C = useControlRoomColors();
  const T = useChartTokens();
  const accent = categoryAccent('hr');

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    hrTimePayrollService
      .commandCenter()
      .then((res: any) => setData(res))
      .catch((e: any) => setError(e?.message || 'Failed to load the Time & Payroll command center.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const period = data?.period || {};
  const payroll = data?.payroll || {};
  const currentRun = payroll.currentRun || null;
  const statusCounts: Record<string, number> = payroll.statusCounts || {};
  const lastPostedRun = payroll.lastPostedRun || null;
  const unpostedProcessedCount: number = payroll.unpostedProcessedCount || 0;
  const costTrend: any[] = data?.costTrend || [];
  const statutory = data?.statutory || null;
  const costByDepartment: any[] = data?.costByDepartment || [];
  const attendanceToday = data?.attendanceToday || {};
  const approvals = data?.approvals || {};
  const attention: any[] = data?.attention || [];

  const pendingApprovalsTotal = (approvals.pendingLeave || 0) + (approvals.pendingRegularizations || 0);

  const kpiDefs = useMemo(
    () => [
      {
        key: 'currentNet',
        label: 'This-Period Net',
        value: fmtMoney(currentRun?.net_total),
        color: C.good,
        onClick: () => navigate('/hr/payroll'),
      },
      {
        key: 'ytdNet',
        label: 'YTD Net Paid',
        value: fmtMoney(payroll.ytdNetPaid),
        color: C.text,
        onClick: () => navigate('/hr/payroll'),
      },
      {
        key: 'headcount',
        label: 'Headcount on Run',
        value: fmtInt(currentRun?.employee_count),
        color: C.cyan,
        onClick: () => navigate('/hr/payroll'),
      },
      {
        key: 'presentToday',
        label: 'Present Today',
        value: isNum(attendanceToday.present) && isNum(attendanceToday.total)
          ? `${fmtInt(attendanceToday.present)}/${fmtInt(attendanceToday.total)}`
          : '—',
        color: C.good,
        onClick: () => navigate('/hr/attendance'),
      },
      {
        key: 'pendingApprovals',
        label: 'Pending Approvals',
        value: fmtInt(pendingApprovalsTotal),
        color: pendingApprovalsTotal > 0 ? C.warn : C.text,
        onClick: () => navigate('/hr/leave'),
      },
      {
        key: 'unpostedRuns',
        label: 'Unposted Runs',
        value: fmtInt(unpostedProcessedCount),
        color: unpostedProcessedCount > 0 ? C.bad : C.text,
        onClick: () => navigate('/hr/payroll'),
      },
    ],
    [C, currentRun, payroll, attendanceToday, pendingApprovalsTotal, unpostedProcessedCount, navigate]
  );

  const Metric: React.FC<{ label: string; value: React.ReactNode; color?: string; onClick?: () => void }> = ({
    label,
    value,
    color,
    onClick,
  }) => (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: C.panel,
        border: `1px solid ${C.line}`,
        borderRadius: 2,
        p: 2,
        minWidth: 0,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.15s',
        '&:hover': onClick ? { borderColor: C.accent } : undefined,
      }}
    >
      <Typography
        sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {label}
      </Typography>
      <Typography
        title={typeof value === 'string' ? value : undefined}
        sx={{
          fontWeight: 800,
          fontSize: { xs: 17, sm: 19, md: 20, lg: 22 },
          lineHeight: 1.15,
          color: color || C.text,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
      {children}
    </Typography>
  );

  const Panel: React.FC<{ title: string; children: React.ReactNode; action?: React.ReactNode }> = ({ title, children, action }) => (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <SectionLabel>{title}</SectionLabel>
        {action}
      </Stack>
      <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2, height: '100%' }}>{children}</Box>
    </Box>
  );

  const EmptyNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ color: C.sub, p: 1 }}>{children}</Typography>
  );

  const StatutoryTile: React.FC<{ label: string; value: React.ReactNode; color: string }> = ({ label, value, color }) => (
    <Box sx={{ bgcolor: C.panel2, border: `1px solid ${C.line}`, borderRadius: 1.5, p: 1.5, minWidth: 0 }}>
      <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </Typography>
      <Typography
        title={typeof value === 'string' ? value : undefined}
        sx={{ fontWeight: 800, fontSize: 18, color, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {value}
      </Typography>
    </Box>
  );

  // ---- Cost trend chart (gross/net bars, headcount line on secondary axis) ----
  const CostTrendChart: React.FC = () => {
    if (!costTrend.length) return <EmptyNote>No trend data available.</EmptyNote>;
    return (
      <Box sx={{ width: '100%', height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={costTrend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid {...gridProps(T)} />
            <XAxis dataKey="month" {...axisProps(T)} minTickGap={10} />
            <YAxis
              yAxisId="money"
              {...axisProps(T)}
              width={64}
              tickFormatter={(v: number) => formatIndianNumber(v)}
            />
            <YAxis yAxisId="headcount" orientation="right" {...axisProps(T)} width={44} allowDecimals={false} />
            <RTooltip
              {...tooltipStyle(T)}
              formatter={(v: any, name: any) => (name === 'Headcount' ? [fmtInt(v), name] : [formatIndianNumber(Number(v)), name])}
            />
            <Legend wrapperStyle={{ fontSize: 11, color: T.textSecondary }} />
            <Bar yAxisId="money" dataKey="gross" name="Gross" fill={T.series(0)} radius={[3, 3, 0, 0]} maxBarSize={22} />
            <Bar yAxisId="money" dataKey="net" name="Net" fill={T.series(1)} radius={[3, 3, 0, 0]} maxBarSize={22} />
            <Line yAxisId="headcount" type="monotone" dataKey="headcount" name="Headcount" stroke={T.series(2)} strokeWidth={2.4} dot={false} activeDot={{ r: 4 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  // ---- Attendance-today donut ----
  const attendanceDonutRows = useMemo(() => {
    const rows = [
      { label: 'Present', count: attendanceToday.present, color: C.good },
      { label: 'Absent', count: attendanceToday.absent, color: C.bad },
      { label: 'On Leave', count: attendanceToday.onLeave, color: C.cyan },
      { label: 'WFH', count: attendanceToday.wfh, color: C.purple },
      { label: 'Half-Day', count: attendanceToday.halfDay, color: C.warn },
      { label: 'Not Marked', count: attendanceToday.notMarked, color: C.sub },
    ];
    return rows.filter((r) => isNum(r.count) && Number(r.count) > 0);
  }, [attendanceToday, C]);

  const AttendanceDonut: React.FC = () => {
    if (!attendanceDonutRows.length) return <EmptyNote>No attendance marked yet today.</EmptyNote>;
    return (
      <Box sx={{ width: '100%', height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={attendanceDonutRows}
              dataKey="count"
              nameKey="label"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={2}
              label={(p: any) => `${p.label} (${p.count})`}
              labelLine={false}
            >
              {attendanceDonutRows.map((r, i) => (
                <Cell key={i} fill={r.color} />
              ))}
            </Pie>
            <RTooltip {...tooltipStyle(T)} />
            <Legend wrapperStyle={{ fontSize: 11, color: T.textSecondary }} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const goto = (link?: string | null) => {
    if (link) navigate(link);
  };

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', m: -3, color: C.text }}>
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${C.line}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          background: `linear-gradient(90deg, ${C.panel}, ${C.bg})`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Payments sx={{ color: accent }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Time &amp; Payroll</Typography>
            <Typography sx={{ color: C.sub, fontSize: 13 }}>{period.label || 'Control room'}</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/hr/attendance')} sx={{ color: C.text, borderColor: C.line }}>
            Attendance
          </Button>
          <Button variant="outlined" onClick={() => navigate('/hr/payroll')} sx={{ color: C.text, borderColor: C.line }}>
            Payroll
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress />}

      {error && !data ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          {/* KPI band */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                md: 'repeat(auto-fit, minmax(150px, 1fr))',
              },
              gap: 1.5,
            }}
          >
            {kpiDefs.map((k) => (
              <Metric key={k.key} label={k.label} value={k.value} color={k.color} onClick={k.onClick} />
            ))}
          </Box>

          {/* Payroll status + cost trend */}
          <Box sx={{ px: 3, pb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.1fr 1.6fr' }, gap: 2 }}>
            <Panel title="Payroll Status">
              <Stack spacing={2}>
                {currentRun ? (
                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 13.5 }} noWrap>
                        {currentRun.run_code || `Run #${currentRun.id}`}
                      </Typography>
                      <StatusDot label={(currentRun.status || '—').replace(/_/g, ' ')} tone={runStatusTone(currentRun.status)} />
                    </Stack>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Typography sx={{ fontSize: 12, color: C.sub }}>
                        Gross: <Box component="span" sx={{ color: C.text, fontWeight: 600 }}>{fmtMoney(currentRun.gross_total)}</Box>
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: C.sub }}>
                        Deductions: <Box component="span" sx={{ color: C.text, fontWeight: 600 }}>{fmtMoney(currentRun.deduction_total)}</Box>
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: C.sub }}>
                        Net: <Box component="span" sx={{ color: C.good, fontWeight: 700 }}>{fmtMoney(currentRun.net_total)}</Box>
                      </Typography>
                    </Stack>
                  </Box>
                ) : (
                  <Typography sx={{ color: C.sub, fontSize: 13, fontStyle: 'italic' }}>
                    No payroll run for {period.label || 'this period'} yet.
                  </Typography>
                )}

                {unpostedProcessedCount > 0 && (
                  <Box
                    onClick={() => navigate('/hr/payroll')}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.25,
                      borderRadius: 1.5,
                      bgcolor: `${C.warn}14`,
                      border: `1px solid ${C.warn}44`,
                      cursor: 'pointer',
                    }}
                  >
                    <WarningAmber sx={{ fontSize: 16, color: C.warn, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12.5, color: C.text, flex: 1 }}>
                      {unpostedProcessedCount} processed run(s) not yet posted to Finance.
                    </Typography>
                    <ChevronRight sx={{ fontSize: 16, color: C.warn }} />
                  </Box>
                )}

                <Box>
                  <Typography sx={{ fontSize: 11, color: C.sub, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Last Posted Run
                  </Typography>
                  {lastPostedRun ? (
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <Typography sx={{ fontSize: 12.5 }} noWrap>
                        {lastPostedRun.run_code || `Run #${lastPostedRun.id}`} · {fmtMoney(lastPostedRun.net_total)} · {fmtDate(lastPostedRun.posted_at)}
                      </Typography>
                      {lastPostedRun.journal_entry_number && lastPostedRun.journal_entry_id ? (
                        <Chip
                          size="small"
                          icon={<Receipt sx={{ fontSize: 14 }} />}
                          label={lastPostedRun.journal_entry_number}
                          onClick={() => navigate(`/finance/journal/${lastPostedRun.journal_entry_id}`)}
                          sx={{ cursor: 'pointer', color: C.cyan, borderColor: C.cyan, bgcolor: `${C.cyan}14` }}
                          variant="outlined"
                        />
                      ) : null}
                    </Stack>
                  ) : (
                    <Typography sx={{ color: C.sub, fontSize: 12, fontStyle: 'italic' }}>No run has been posted yet.</Typography>
                  )}
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 11, color: C.sub, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Runs by Status
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {Object.keys(statusCounts).length ? (
                      Object.entries(statusCounts).map(([status, count]) => (
                        <Chip
                          key={status}
                          size="small"
                          label={`${status.replace(/_/g, ' ')}: ${fmtInt(count)}`}
                          sx={{ fontSize: 10.5, height: 22, bgcolor: C.panel2, color: C.text }}
                        />
                      ))
                    ) : (
                      <Typography sx={{ color: C.sub, fontSize: 12 }}>—</Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Panel>

            <Panel title="Cost Trend (6 months)">
              <CostTrendChart />
            </Panel>
          </Box>

          {/* Statutory + Cost by department */}
          <Box sx={{ px: 3, pb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1.2fr' }, gap: 2 }}>
            <Panel title="Statutory Liabilities">
              {statutory ? (
                <Stack spacing={1.5}>
                  <Typography sx={{ color: C.sub, fontSize: 11 }}>For {statutory.runLabel}</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 1.25 }}>
                    <StatutoryTile label="PF Payable" value={fmtMoney(statutory.totalPfPayable)} color={C.good} />
                    <StatutoryTile label="ESI Payable" value={fmtMoney(statutory.totalEsiPayable)} color={C.cyan} />
                    <StatutoryTile label="TDS" value={fmtMoney(statutory.tds)} color={C.warn} />
                    <StatutoryTile label="Professional Tax" value={fmtMoney(statutory.professionalTax)} color={C.purple} />
                  </Box>
                </Stack>
              ) : (
                <TableEmptyState title="No processed run yet" hint="Statutory liabilities appear once a payroll run has payslips." />
              )}
            </Panel>

            <Panel title="Cost by Department">
              {costByDepartment.length ? (
                <TableSurface>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Department</TableCell>
                        <TableCell sx={headerCellSx} align="right">Headcount</TableCell>
                        <TableCell sx={headerCellSx} align="right">Monthly Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {costByDepartment.map((row: any, i: number) => (
                        <TableRow key={i} sx={bodyRowSx}>
                          <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.department}>
                            {row.department || '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtInt(row.headcount)}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} title={fmtMoney(row.monthlyCost)}>
                            {fmtMoney(row.monthlyCost)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableSurface>
              ) : (
                <TableEmptyState title="No department cost data" hint="Requires active employees with a CTC and department assigned." />
              )}
            </Panel>
          </Box>

          {/* Attendance today + Attention */}
          <Box sx={{ px: 3, pb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.4fr' }, gap: 2 }}>
            <Panel
              title="Attendance Today"
              action={
                <Button size="small" endIcon={<ChevronRight />} onClick={() => navigate('/hr/attendance')} sx={{ color: C.accent }}>
                  Details
                </Button>
              }
            >
              <AttendanceDonut />
            </Panel>

            <Box>
              <SectionLabel>Attention Required</SectionLabel>
              <Stack spacing={1}>
                {attention.map((a: any, i: number) => (
                  <Box
                    key={i}
                    sx={{
                      bgcolor: C.panel,
                      border: `1px solid ${C.line}`,
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <AccessTime sx={{ fontSize: 14, color: severityTone(C, a.severity), flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap title={a.label}>
                        {a.label || '—'}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      endIcon={<ChevronRight />}
                      disabled={!a.link}
                      onClick={() => goto(a.link)}
                      sx={{ color: C.accent, flexShrink: 0 }}
                    >
                      Open
                    </Button>
                  </Box>
                ))}
                {!loading && !attention.length && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <WarningAmber sx={{ fontSize: 16, color: C.sub }} />
                    <Typography sx={{ color: C.sub, fontStyle: 'italic' }}>Nothing needs attention.</Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TimePayrollCommandCenter;
